
  import { computed, defineComponent, onMounted, ref, watch } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRoute, useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import Multiselect from '@vueform/multiselect';
  import moment from 'moment';

  export default defineComponent({
    name: 'blogs-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
      Multiselect,
    },
    async setup() {
      const { t, te } = useI18n();
      const { can } = useAbility();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const onSubmitPullDataButton = ref<HTMLButtonElement | null>(null);
      const blogData = ref<any>(null);
      const thumbnail = ref('/media/avatars/blank-image.jpg');
      const authorAvatarThumbnail = ref('/media/avatars/blank-image.jpg');
      const allTags = computed(() => store.getters.allListBlogsTags || []);
      const selectedBlogsIds = ref<number[]>([]);
      const allBlogs = computed(() => store.getters.getAllBlogsList || []);
      const allBlogCategories = computed(
        () => store.getters.allListBlogCategories || []
      );
      const isScheduled = ref(false);
      let selectedTagsIds = ref<number[]>([]);

      const isArticlesCategory = (categoryId) =>
        allBlogCategories.value.some(
          (category) =>
            category.id === categoryId && category.slug === 'Articles'
        );

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      watch(isScheduled, (newValue) => {
        if (blogData.value) {
          if (newValue) {
            blogData.value.status = 'SCHEDULE';
          } else {
            blogData.value.status = 'PUBLISH';
            blogData.value.scheduledAt = null;
          }
        }
      });

      const goBack = () => {
        router.back();
      };

      const onSubmitPullData = async (values) => {
        if (onSubmitPullDataButton.value) {
          // eslint-disable-next-line
        onSubmitPullDataButton.value!.disabled = true;
          onSubmitPullDataButton.value.setAttribute('data-kt-indicator', 'on');
        }
        const data = await store.dispatch(Actions.PULL_URL_NEWS, {
          url: blogData.value.externalReference,
        });

        blogData.value.titleAr = data.title;
        blogData.value.titleEn = data.title;
        blogData.value.descriptionAr = data.description;
        blogData.value.descriptionEn = data.description;
        blogData.value.image = data.image;
        // Use the article's own publish date when the link has one
        // (`date` for al-Qabas links, `publishedAt` for the rest).
        const pulledDate = moment(data?.publishedAt || data?.date);
        if ((data?.publishedAt || data?.date) && pulledDate.isValid()) {
          blogData.value.publishedAt = pulledDate.format('YYYY-MM-DDTHH:mm');
        }
        blogData.value.authorAvatar = data.authorAvatar;
        thumbnail.value = data.image;

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          onSubmitPullDataButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
        onSubmitPullDataButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_PULLED_DATA'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            onSubmitPullDataButton.value?.removeAttribute('data-kt-indicator');
            // eslint-disable-next-line
          onSubmitPullDataButton.value!.disabled = false;
          });
        }
      };

      const goToBlogContent = (id: number) => {
        router.push({ name: 'blogs-content', params: { id } });
      };

      const previewImage = (e, type: 'cover' | 'authorAvatar' = 'cover') => {
        const file = e.target.files[0];
        if (file) {
          const previewUrl = URL.createObjectURL(file);
          if (type === 'cover') {
            thumbnail.value = previewUrl;
            blogData.value.image = file;
          } else if (type === 'authorAvatar') {
            authorAvatarThumbnail.value = previewUrl;
            blogData.value.authorAvatar = file;
          }
        }
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('blogsUpdating'), [
          translate('blogs'),
        ]);
        await store.dispatch(Actions.GET_ALL_BLOGS);
        await store.dispatch(Actions.GET_ALL_BLOG_TAGS);
        await store.dispatch(Actions.GET_ALL_BLOG_CATEGORIES);
        const associatedTags = await store.dispatch(
          Actions.GET_BLOG_ASSOCIATED_TAGS,
          route.params.id
        );
        selectedTagsIds.value = associatedTags.data.data.map((tag) => tag.id);
        thumbnail.value =
          blogData.value.imageUrl || '/media/avatars/blank-image.jpg';

        authorAvatarThumbnail.value =
          blogData.value.authorAvatar || '/media/avatars/blank-image.jpg';
      });

      const { data } = await store.dispatch(Actions.GET_BLOG, route.params.id);
      blogData.value = data.data;
      isScheduled.value = blogData.value.status == 'SCHEDULE' ? true : false;

      if (data.data.scheduledAt)
        data.data.scheduledAt = moment(data.data.scheduledAt).format(
          'YYYY-MM-DDTHH:mm'
        );
      data.data.publishedAt = data.data.publishedAt
        ? moment(data.data.publishedAt).format('YYYY-MM-DDTHH:mm')
        : '';

      if (blogData.value?.relatedIds) {
        selectedBlogsIds.value = blogData.value.relatedIds.slice();
      }
      if (blogData.value?.tagIds) {
        selectedTagsIds.value = blogData.value.tagIds.slice();
      }

      const blogSchema = Yup.object().shape({
        titleAr: Yup.string().required(translate('AR_TITLE_IS_REQUIRED')),
        titleEn: Yup.string().required(translate('EN_TITLE_IS_REQUIRED')),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        if (values.image instanceof File) {
          const coverImageFormData = new FormData();
          coverImageFormData.append('image', values.image);

          await store.dispatch(Actions.UPLOAD_BLOG_COVER_IMAGE, {
            blogId: route.params.id,
            data: coverImageFormData,
          });
          delete values.image;
        }
        if (values.authorAvatar) {
          const authorAvatarFormData = new FormData();
          authorAvatarFormData.append('author', values.authorAvatar);

          await store.dispatch(Actions.UPLOAD_BLOG_AUTHOR_AVATAR, {
            blogId: route.params.id,
            data: authorAvatarFormData,
          });
          delete values.authorAvatar;
        }
        if (typeof values.isHomepage === 'string') {
          values.isHomepage = values.isHomepage === 'true';
        }
        if (values.scheduledAt) {
          values.scheduledAt = new Date(values.scheduledAt);
        }
        if (values.publishedAt) {
          values.publishedAt = moment(values.publishedAt).toISOString();
        } else {
          delete values.publishedAt;
        }

        if (values.externalReference) {
          values.isExternal = true;
        } else {
          values.isExternal = false;
        }

        const updatePayload = { ...values };

        if (typeof blogData.value.image === 'string' && blogData.value.image) {
          updatePayload.image = blogData.value.image;
        }

        if (isArticlesCategory(blogData.value.categoryId)) {
          updatePayload.addToTestimonials = Boolean(
            blogData.value.addToTestimonials
          );
        }

        await store.dispatch(Actions.UPDATE_BLOG, {
          id: blogData.value?.id,
          data: updatePayload,
        });

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgain'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          if (submitButton.value) submitButton.value.disabled = false;
        } else {
          Swal.fire({
            text: translate('BLOG_UPDATE_SUCCESS'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            submitButton.value?.removeAttribute('data-kt-indicator');
            if (submitButton.value) submitButton.value.disabled = false;
            router.push({ name: 'blogs-listing' });
          });
        }
      };

      return {
        blogData,
        submitButton,
        onSubmitUpdate,
        translate,
        blogSchema,
        goBack,
        can,
        thumbnail,
        authorAvatarThumbnail,
        previewImage,
        goToBlogContent,
        allBlogs,
        selectedBlogsIds,
        allTags,
        selectedTagsIds,
        isScheduled,
        allBlogCategories,
        isArticlesCategory,
        onSubmitPullDataButton,
        onSubmitPullData,
      };
    },
  });
